import { useEffect, useState } from 'react'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { IoHandLeftOutline } from 'react-icons/io5'
import { ImageUploadUrl } from 'types/graphql'

import { useMutation } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import {
  CommonArrayItem,
  LivestreamInputType,
  MetadataItemProps,
} from 'src/common/types'
import Button from 'src/components/Custom/Button/Button'
import ConfigureLivestreamModal from 'src/components/Custom/Modal/ConfigureLivestreamModal'
import PrimaryModal from 'src/components/Custom/Modal/PrimaryModal'
import UpdatableText from 'src/components/Custom/UpdatableText/UpdatableText'
import {
  CREATE_IMAGE_UPLOAD_URL_MUTATION,
  uploadImageToCloudflare,
} from 'src/lib/images'
import { nonEmptyStringRule } from 'src/lib/validationRules'

import {
  embedCodeFieldDescription,
  pbsStationManagerLivestreamFeedCidFieldDescription,
  sourceTabs,
  sourceUrlFieldDescription,
  vimeoLiveEventIdFieldDescription,
} from './constants'
import LiveItem from './LiveItem'
import PbsStationManagerField from './PbsStationManagerField'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export interface Livestreams {
  channels: Array<Livestream>
}

export interface Livestream {
  id: string
  label: string
  logo: string
  name: string
  shortStationDescription: string | null
  slug: string
  source: {
    embedCode: string | null
    pbsStationManager: string | null
    sourceUrl: string | null
    vimeoLiveEventId: string | null
  }
  stationDescription: string | null
  streamScheduleId?: string | null
  weight: number
}

export interface LiveProps {
  data: Livestreams
  onChange: (data: Livestreams) => void
  updateLivestream: (
    id: string,
    inputData: LivestreamInputType,
    showAlerts?: boolean
  ) => Promise<void>
  createLivestream: (inputData: LivestreamInputType) => Promise<void>
  deleteLivestream: (id: string) => Promise<void>
  stationId: string
  loadingCreateLivestream: boolean
  loadingUpdateLivestream: boolean
  stationManagerLivestreamOptions: MetadataItemProps[]
  streamScheduleOptions: CommonArrayItem[]
}

const MAX_CHANNELS = 10

const Live = ({
  data,
  onChange,
  updateLivestream,
  createLivestream,
  deleteLivestream,
  stationId,
  loadingCreateLivestream,
  loadingUpdateLivestream,
  stationManagerLivestreamOptions,
  streamScheduleOptions,
}: LiveProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    const activeIndex = active.data.current.sortable.index
    const overIndex = over?.data.current.sortable.index

    if (activeIndex === overIndex) return

    const weights = data.channels.map((channel) => ({
      id: channel.id,
      weight: channel.weight,
    }))
    const updatedWeights = arrayMove(weights, activeIndex, overIndex).map(
      (item, index) => ({ ...item, weight: index })
    )

    toast.promise(
      Promise.all(
        updatedWeights.map(async (item) => {
          await updateLivestream(
            item.id,
            {
              weight: item.weight,
            },
            false
          )
        })
      ),
      {
        loading: 'Saving order...',
        success: 'Order saved',
        error: 'Something went wrong',
      }
    )
  }

  const [channelDeleteId, setChannelDeleteId] = useState(null)

  const [activeSourcesTab, setActiveSourcesTab] = useState(() => {
    const initialTabs = {}
    data.channels.forEach((item) => {
      initialTabs[item.id] = 0
    })
    return initialTabs
  })

  const [showModal, setShowModal] = useState(false)
  const [showPrimaryModal, setShowPrimaryModal] = useState(false)

  const updateCurrentChannel = (
    value: string | null,
    key: string,
    item: Livestream
  ) => {
    const newData = { ...data }
    const currentChannelData = item

    if (
      key === 'embedCode' ||
      key === 'sourceUrl' ||
      key === 'pbsStationManager' ||
      key === 'vimeoLiveEventId'
    ) {
      currentChannelData.source[key] = value ?? ''
    } else {
      currentChannelData[key] = value
    }

    onChange(newData)
  }

  const deleteCurrentChannel = () => {
    deleteLivestream(channelDeleteId).then(() => {
      setChannelDeleteId(null)
    })
  }

  const addChannel = () => {
    if (data.channels.length < MAX_CHANNELS) {
      setShowModal(true)
    }
  }

  const [createImageUploadUrl] = useMutation<{
    createImageUploadUrl: ImageUploadUrl
  }>(CREATE_IMAGE_UPLOAD_URL_MUTATION)

  const uploadImage = async (files: File[]) => {
    const mutationResult = await createImageUploadUrl()
    const { uploadUrl } = mutationResult.data.createImageUploadUrl
    return uploadImageToCloudflare(uploadUrl, files[0])
  }

  const handleImageUpload = async (files: File[], item) => {
    await uploadImage(files).then((newImageUrl) => {
      updateLivestream(item.id, {
        logo: newImageUrl,
      }).then(() => {
        const _data = { ...data }
        item.logo = newImageUrl
        onChange(_data)
      })
    })
  }

  useEffect(() => {
    setActiveSourcesTab((currentTabs) => {
      const updatedTabs = { ...currentTabs }
      data.channels.forEach((item) => {
        if (updatedTabs[item.id] === undefined) {
          updatedTabs[item.id] = 0
        }
      })
      return updatedTabs
    })
  }, [data.channels])

  const renderLivestreamSourceFields = (item: Livestream) => {
    const { source } = item
    const activeTabIndex =
      activeSourcesTab[item.id] !== undefined ? activeSourcesTab[item.id] : 0
    const selectedTabName = sourceTabs[activeTabIndex]?.name
    const selectedData = source[selectedTabName]

    return (
      <div key={selectedTabName} className="mt-4 w-full space-y-4">
        {
          <div className="w-full">
            <span className="font-medium text-gray-700">
              {selectedTabName == 'embedCode'
                ? 'Stream Embed Code'
                : selectedTabName == 'sourceUrl'
                  ? 'Stream URL'
                  : selectedTabName === 'vimeoLiveEventId'
                    ? 'Vimeo Live Event ID'
                    : 'PBS Station Manager Feed'}
            </span>
            {selectedTabName === 'pbsStationManager' ? (
              <>
                <PbsStationManagerField
                  loadingData={loadingUpdateLivestream}
                  onChange={(v, k) => updateCurrentChannel(v, k, item)}
                  onSave={() => {
                    return updateLivestream(item.id, {
                      pbsStationManagerLivestreamFeedCid: selectedData,
                    })
                  }}
                  selectedData={selectedData}
                  stationManagerLivestreamOptions={
                    stationManagerLivestreamOptions
                  }
                  validationRules={
                    !item.source.embedCode &&
                    !item.source.vimeoLiveEventId &&
                    !item.source.sourceUrl &&
                    nonEmptyStringRule('Please select a feed from dropdown.')
                  }
                />
              </>
            ) : (
              <UpdatableText
                value={selectedData}
                onChange={(v) => updateCurrentChannel(v, selectedTabName, item)}
                loadingData={loadingUpdateLivestream}
                onSave={() => {
                  const updatePayload = {
                    ...(selectedTabName === 'embedCode' && {
                      embedCode: selectedData,
                    }),
                    ...(selectedTabName === 'sourceUrl' && {
                      sourceUrl: selectedData,
                    }),
                    ...(selectedTabName === 'vimeoLiveEventId' && {
                      vimeoLiveEventId: selectedData,
                    }),
                  }
                  return updateLivestream(item.id, updatePayload)
                }}
                {...((selectedTabName === 'embedCode' &&
                  !item.source.sourceUrl &&
                  !item.source.pbsStationManager &&
                  !item.source.vimeoLiveEventId) ||
                (selectedTabName === 'sourceUrl' &&
                  !item.source.embedCode &&
                  !item.source.pbsStationManager &&
                  !item.source.vimeoLiveEventId) ||
                (selectedTabName === 'vimeoLiveEventId' &&
                  !item.source.embedCode &&
                  !item.source.pbsStationManager &&
                  !item.source.sourceUrl)
                  ? { validationRules: nonEmptyStringRule() }
                  : {})}
              />
            )}
            {selectedTabName == 'embedCode'
              ? embedCodeFieldDescription
              : selectedTabName == 'sourceUrl'
                ? sourceUrlFieldDescription
                : selectedTabName == 'vimeoLiveEventId'
                  ? vimeoLiveEventIdFieldDescription
                  : pbsStationManagerLivestreamFeedCidFieldDescription}
          </div>
        }
      </div>
    )
  }

  return (
    <div className="w-full space-y-2">
      <div className="my-4 flex items-center justify-end space-x-2">
        <div className="flex flex-1 flex-row items-center">
          <IoHandLeftOutline className="mr-2" />
          <p className="text-sm font-normal text-gray-900">
            Drag and drop to reorder.
          </p>
        </div>
        <div className="flex flex-col">
          <Button
            title="Add another livestream"
            onClick={addChannel}
            isDisabled={data.channels.length === MAX_CHANNELS}
          />
          {data.channels.length === MAX_CHANNELS && (
            <p className="mt-2 text-right text-xs text-red-600">
              A maximum of {MAX_CHANNELS} items is permitted.
            </p>
          )}
        </div>
      </div>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={data.channels}
          strategy={verticalListSortingStrategy}
        >
          {data.channels.length > 0 &&
            data.channels
              .sort((a, b) => (a.weight > b.weight ? 1 : -1))
              .map((item) => {
                return (
                  <LiveItem
                    handleImageUpload={handleImageUpload}
                    isLoading={loadingUpdateLivestream}
                    item={item}
                    key={item.id}
                    onDelete={() => {
                      setShowPrimaryModal(true)
                      setChannelDeleteId(item.id)
                    }}
                    renderLivestreamSourceFields={renderLivestreamSourceFields}
                    streamScheduleOptions={streamScheduleOptions}
                    setActiveSourcesTab={setActiveSourcesTab}
                    updateCurrentChannel={updateCurrentChannel}
                    updateLivestream={updateLivestream}
                  />
                )
              })}
        </SortableContext>
      </DndContext>
      {showModal && (
        <ConfigureLivestreamModal
          showModal={showModal}
          handleModalClose={() => setShowModal(false)}
          sourceTabs={sourceTabs}
          classNames={classNames}
          onSave={createLivestream}
          uploadImage={uploadImage}
          stationId={stationId}
          loadingCreateLivestream={loadingCreateLivestream}
          streamScheduleOptions={streamScheduleOptions}
          stationManagerLivestreamOptions={stationManagerLivestreamOptions}
        />
      )}

      <PrimaryModal
        modalText={'Are you sure you wish to remove this channel?'}
        buttonList={[
          {
            label: 'Remove',
            id: 'remove',
            type: 'primary',
            onClick: () => {
              deleteCurrentChannel()
              setShowPrimaryModal(false)
            },
          },
          {
            label: 'Cancel',
            id: 'cancel',
            type: 'secondary',
            onClick: () => setShowPrimaryModal(false),
          },
        ]}
        showModal={showPrimaryModal}
        setShowModal={setShowPrimaryModal}
      />
    </div>
  )
}

export default Live
